import { useEffect, useRef, useState } from "react";
import { ReactComponent as Info } from "../../../../../../assets/images/icons/info.svg";
import { ReactComponent as Copy } from "../../../../../../assets/images/svg/document-copy.svg";
import { ReactComponent as Edit } from "../../../../../../assets/images/icons/edit-2.svg";
import { ReactComponent as Trash } from "../../../../../../assets/images/icons/trash-red.svg";
import { isInt, isNegative, secondsToDhm } from "../../../../../../scripts/helpers";
import { advancedResponseValidator, del, get, patch, post, responseValidator } from "../../../../../../scripts/api";
import API from "../../../../../../urls/api";
import { handleSetPublicLink } from "../../../fileManager.actions";
import ConfirmationModal from "../../../../confirmationModal/confirmationModal.index";
import NumberInput from "../../../../numberInput/numberInput.index";
import Dropdown from "../../../../dropdown/dropDown.index";
import Toggle from "../../../../toggle/toggle.index";
import Button from "../../../../button/button.index";
import Dialog from "../../../../dialog/dialog.index";
import { syncFileManager } from "../../../fileManager.function";
import { toast } from "mobin-toast";
import { toastMessages } from "../../../../../../scripts/toastMessages";
import { shallowEqual } from "react-redux";
import { useAppSelector } from "../../../../../../redux/hooks";
import { ENVIRONMENT } from "../../../../../../environment";
import { ReactComponent as InfoCircle } from "../../../../../../assets/images/icons/info-circle.svg";
import { PublicLink } from "../../../../../../interfaces/general";
import CheckBox from "../../../../checkbox/checkbox.index";
import PublicLinkApproved from "./publicLinkApproved";
import Loader from "../../../../loader/loader.index";

const publicLinkDropDownOptions = [
    { label: "لینک بدون انقضا", payload: 1 },
    { label: "لینک زمان‌دار", payload: 2 }
];

const PublicLinkModal = ({
    showPublicLink,
    setShowPublicLink,
    data,
    onUpdate,
    onClose
}: {
    showPublicLink?: boolean;
    setShowPublicLink?: (showPublicLink: boolean) => void;
    data?: any;
    onUpdate?: () => void;
    mode?: any;
    onClose?: () => void;
}) => {
    const [approve, setApprove] = useState(false);
    const buttonRef = useRef<any>();
    const hiddenInput = useRef<any>();
    const { selectedResources, informationModalStatus, publicLinkModalStatus } = useAppSelector(
        ({ fileManager: { selectedResources, informationModalStatus, publicLinkModalStatus } }) => ({
            selectedResources,
            informationModalStatus,
            publicLinkModalStatus
        }),
        shallowEqual
    );
    const [step, setStep] = useState<number>(1);
    const [selected, setSelected] = useState(publicLinkDropDownOptions[0]);
    const [enabled, setEnabled] = useState<boolean>(true);
    const [showRemovePublicLinkModal, setShowRemovePublicLinkModal] = useState<boolean>(false);
    const [showPublicLinkEditModal, setShowPublicLinkEditModal] = useState<boolean>(false);
    const [info, setInfo] = useState<any>({
        minute: data ? secondsToDhm(data.duration * 60)[2] : 0,
        hour: data ? secondsToDhm(data.duration * 60)[1] : 0,
        day: data ? secondsToDhm(data.duration * 60)[0] || 0 : 1,
        expiration_count: data ? data.expiration_count : null,
        id: data ? data.id : "",
        link: "",
        duration: data ? data.duration : null
    });
    const [publicLinks, setPublicLinks] = useState<PublicLink[] | undefined>(undefined);

    useEffect(() => {
        if (hiddenInput.current) hiddenInput.current.focus();
    });

    useEffect(() => {
        if (data) {
            if (data.duration !== null) setSelected(publicLinkDropDownOptions[1]);
            if (!!data.expiration_count) setEnabled(false);
        }
    }, []);

    useEffect(() => {
        if (publicLinkModalStatus || showPublicLink) {
            get((data, status) => {
                if (responseValidator(status)) {
                    setPublicLinks(data.public_links);
                }
            }, API.fileManager.getObject(selectedResources[0].id));
        }
    }, [publicLinkModalStatus, showPublicLink]);

    useEffect(() => {
        setInfo({
            ...info,
            minute: data ? secondsToDhm(data.duration * 60)[2] : 0,
            hour: data ? secondsToDhm(data.duration * 60)[1] : 0,
            day: data ? secondsToDhm(data.duration * 60)[0] || 0 : 1,
            expiration_count: data ? data.expiration_count : null,
            id: data ? data.id : ""
        });
    }, [data]);

    const [formErrors, setFormErrors] = useState({
        minute: "",
        hour: "",
        day: "",
        time: "",
        expiration_count: ""
    });

    const handleFormValidation = (value: any, type: string) => {
        let isValidate = false;
        switch (type) {
            case "minute":
                if (!isInt(value)) setFormErrors({ ...formErrors, minute: "دقیقه را درست وارد کنید!" });
                else if (isNegative(value)) setFormErrors({ ...formErrors, minute: "دقیقه منفی نمی‌توانید وارد کنید!" });
                else if (value > 59) setFormErrors({ ...formErrors, minute: "حداکثر ۵۹ دقیقه می‌توانید وارد کنید!" });
                else if (value === 0 && info.hour === 0 && info.day === 0)
                    setFormErrors({
                        ...formErrors,
                        time: "روز و ساعت و دقیقه هم‌زمان نمی‌توانند صفر باشند!"
                    });
                else {
                    setFormErrors({ ...formErrors, minute: "", time: "" });
                    isValidate = true;
                }
                break;
            case "hour":
                if (!isInt(value)) setFormErrors({ ...formErrors, hour: "ساعت را درست وارد کنید!" });
                else if (isNegative(value)) setFormErrors({ ...formErrors, hour: "ساعت منفی نمی‌توانید وارد کنید!" });
                else if (value > 23) setFormErrors({ ...formErrors, hour: "حداکثر ۲۳ ساعت می‌توانید وارد کنید!" });
                else if (value === 0 && info.minute === 0 && info.day === 0)
                    setFormErrors({
                        ...formErrors,
                        time: "روز و ساعت و دقیقه هم‌زمان نمی‌توانند صفر باشند!"
                    });
                else {
                    setFormErrors({ ...formErrors, hour: "", time: "" });
                    isValidate = true;
                }
                break;
            case "day":
                if (!isInt(value)) setFormErrors({ ...formErrors, day: "روز را درست وارد کنید!" });
                else if (isNegative(value)) setFormErrors({ ...formErrors, day: "روز منفی نمی‌توانید وارد کنید!" });
                else if (value === 0 && info.minute === 0 && info.hour === 0)
                    setFormErrors({
                        ...formErrors,
                        time: "روز و ساعت و دقیقه هم‌زمان نمی‌توانند صفر باشند!"
                    });
                else {
                    setFormErrors({ ...formErrors, day: "", time: "" });
                    isValidate = true;
                }
                break;
            case "expiration_count":
                if (!isInt(value)) setFormErrors({ ...formErrors, expiration_count: "تعداد دانلود را درست وارد کنید!" });
                else if (value < 1)
                    setFormErrors({
                        ...formErrors,
                        expiration_count: "تعداد دانلود نمی‌تواند کمتر از ۱ باشد!"
                    });
                else {
                    setFormErrors({ ...formErrors, expiration_count: "" });
                    isValidate = true;
                }
                break;
        }
        return isValidate;
    };

    const handleChangeValue = (value: any, type: string) => {
        switch (type) {
            case "minute":
                setInfo({ ...info, minute: value });
                break;
            case "hour":
                setInfo({ ...info, hour: value });
                break;
            case "day":
                setInfo({ ...info, day: value });
                break;
            case "expiration_count":
                if (value >= 1) setInfo({ ...info, expiration_count: value });
                break;
        }
    };

    const handleCreateLink = () => {
        if (info.minute === 0 && info.hour === 0 && info.day === 0)
            return setFormErrors({
                ...formErrors,
                time: "روز و ساعت و دقیقه هم‌زمان نمی‌توانند صفر باشند!"
            });

        if (Object.values(formErrors).every((item) => item === "")) {
            return new Promise((resolve) => {
                post(
                    (data, status) => {
                        if (advancedResponseValidator(data, status)) {
                            setInfo({ ...info, id: data.id, link: data.link, expiration_count: data.expiration_count, duration: data.duration });
                            setStep(2);
                            // if (!selectedResources[0].has_link) syncFileManager();
                            if (onUpdate) onUpdate();
                            toast({ title: toastMessages(31), type: "success" });
                        }
                        resolve(true);
                    },
                    API.fileManager.createPublicLink(),
                    {
                        obj_id: selectedResources[0].id,
                        duration: selected.payload === 1 ? null : Number(info.minute) + Number(info.hour * 60) + Number(info.day * 24 * 60),
                        expiration_count: enabled ? null : info.expiration_count
                    }
                );
            });
        }
    };

    const handleDeletePublicLink = () => {
        del(async (data, status) => {
            if (advancedResponseValidator(data, status)) {
                toast({ title: toastMessages(32), type: "success" });
                syncFileManager();
                setShowRemovePublicLinkModal(false);
                handleClosePublicLinkModal();
            }
        }, API.fileManager.deletePublicLink(info.id));
    };

    const handleEditPublicLink = (publicLinkObj: any) => {
        setStep(1);
        setShowPublicLinkEditModal(true);
    };

    const handleCopyPublicLink = (link: string, closeModal = true) => {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(link);
            toast({ title: toastMessages(33), type: "success" });
        }

        if (closeModal) handleClosePublicLinkModal();
    };

    const handleEditLink = () => {
        if (info.duration !== null && info.minute === 0 && info.hour === 0 && info.day === 0)
            return setFormErrors({
                ...formErrors,
                time: "روز و ساعت و دقیقه هم‌زمان نمی‌توانند صفر باشند!"
            });

        if (Object.values(formErrors).every((item) => item === "")) {
            return new Promise((resolve) => {
                patch(
                    (data, status) => {
                        resolve(true);
                        if (advancedResponseValidator(data, status)) {
                            toast({ title: toastMessages(34), type: "success" });
                            setInfo({
                                minute: secondsToDhm(data.duration * 60)[2],
                                hour: secondsToDhm(data.duration * 60)[1],
                                day: secondsToDhm(data.duration * 60)[0],
                                expiration_count: data.expiration_count,
                                id: data.id,
                                link: data.link,
                                duration: data.duration
                            });
                            setStep(2);
                            if (onUpdate) onUpdate();
                        }
                    },
                    API.fileManager.editPublicLink(info.id),
                    {
                        duration:
                            selected.payload === 1
                                ? null
                                : (!!info.minute ? info.minute : 0) + (!!info.hour ? info.hour : 0) * 60 + (!!info.day ? info.day : 0) * 24 * 60,
                        expiration_count: enabled ? null : info.expiration_count
                    }
                );
            });
        }
    };

    const handleClosePublicLinkModal = () => {
        setApprove(false);
        setStep(1);
        if (!informationModalStatus) handleSetPublicLink(false);
        if (setShowPublicLink) setShowPublicLink(false);
        setSelected(publicLinkDropDownOptions[0]);
        setEnabled(true);
        setShowRemovePublicLinkModal(false);
        setInfo({
            minute: 0,
            hour: 0,
            day: 1,
            expiration_count: null,
            id: "",
            duration: null
        });
        setFormErrors({
            minute: "",
            hour: "",
            day: "",
            time: "",
            expiration_count: ""
        });
        setShowPublicLinkEditModal(false);
        onClose && onClose();
    };

    const fileHasLinkCapacity = () => {
        if (publicLinks && publicLinks.length < ENVIRONMENT.linkMaxCount) {
            return true;
        } else {
            return false;
        }
    };

    const publicLinksCount = () => {
        if (publicLinks) {
            return publicLinks.length;
        }
    };

    return (
        <>
            {showRemovePublicLinkModal && (
                <ConfirmationModal
                    modalType={"DELETE"}
                    status={showRemovePublicLinkModal}
                    onClose={() => setShowRemovePublicLinkModal(false)}
                    onSubmit={handleDeletePublicLink}
                    text="آیا از حذف لینک مطمئن هستید؟"
                    cancelButtonText="خیر"
                    submitButtonText="بله، حذف شود."
                />
            )}
            <Dialog
                status={publicLinkModalStatus ? publicLinkModalStatus : showPublicLink ? showPublicLink : false}
                onClose={handleClosePublicLinkModal}
                title={data || showPublicLinkEditModal ? "ویرایش لینک دسترسی" : "ایجاد لینک دسترسی"}
            >
                <div className="p-4">
                    {selectedResources?.[0]?.cp_approved === undefined ? (
                        <div className="flex items-center justify-center min-h-[270px]">
                            <Loader />
                        </div>
                    ) : selectedResources?.[0]?.cp_approved !== false ? (
                        <>
                            {step === 1 ? (
                                <div
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.stopPropagation();
                                            buttonRef.current.click();
                                        } else if (e.key === "Escape") {
                                            handleClosePublicLinkModal();
                                        }
                                    }}
                                >
                                    <button autoFocus={true} ref={hiddenInput} className="w-0 h-0 absolute outline-none" />
                                    {!data && !showPublicLinkEditModal && publicLinks ? (
                                        <>
                                            {fileHasLinkCapacity() ? (
                                                <div className="flex items-center justify-start gap-2 bg-new-orange-100 p-3 rounded-xl my-4">
                                                    <InfoCircle className="self-start min-w-5 min-h-5 h-5 mt-1" />
                                                    <div className="text-secondary-600 text-base leading-7">
                                                        شما می‌توانید <span className="text-slate-800 font-bold">{ENVIRONMENT.linkMaxCount}</span>{" "}
                                                        لینک بسازید که در حال حاضر{" "}
                                                        <span className="text-slate-800 font-bold">{publicLinksCount()}</span> لینک ساخته شده است.
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-start gap-2 bg-red-200 py-3 px-4 rounded-xl my-4">
                                                    <InfoCircle className="self-start min-w-[16px] min-h-[16px]" />
                                                    <p className="text-secondary-600">
                                                        ظرفیت ساخت لینک برای این فایل تکمیل شده است. شما می‌توانید با حذف لینک‌های ساخته شده، لینک
                                                        جدید ایجاد کنید.
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    ) : null}
                                    <Dropdown
                                        title="نوع لینک"
                                        value={selected}
                                        classNames="mt-2 mb-2 py-3 pr-2"
                                        options={publicLinkDropDownOptions}
                                        onChange={setSelected}
                                    />
                                    {selected.payload === 2 && (
                                        <>
                                            <div className="flex gap-4 justify-end mt-2">
                                                <div>
                                                    <p className="mr-2">دقیقه</p>
                                                    <NumberInput
                                                        inputClassName="w-[78px] text-[15px]"
                                                        value={info.minute}
                                                        onChange={(value) => handleChangeValue(Number(value), "minute")}
                                                        onBlur={(value) => handleFormValidation(Number(value), "minute")}
                                                        error={formErrors.minute}
                                                        showError={false}
                                                    />
                                                </div>
                                                <div>
                                                    <p className="mr-2">ساعت</p>
                                                    <NumberInput
                                                        inputClassName="w-[78px]"
                                                        value={info.hour}
                                                        onChange={(value) => handleChangeValue(Number(value), "hour")}
                                                        onBlur={(value) => handleFormValidation(Number(value), "hour")}
                                                        error={formErrors.hour}
                                                        showError={false}
                                                    />
                                                </div>
                                                <div>
                                                    <p className="mr-2">روز</p>
                                                    <NumberInput
                                                        inputClassName="w-[78px]"
                                                        value={info.day}
                                                        onChange={(value) => handleChangeValue(Number(value), "day")}
                                                        onBlur={(value) => handleFormValidation(Number(value), "day")}
                                                        error={formErrors.day}
                                                        showError={false}
                                                    />
                                                </div>
                                            </div>

                                            {formErrors.day === "" && formErrors.hour === "" && formErrors.minute === "" && formErrors.time === "" ? (
                                                <div className="flex items-center mt-4">
                                                    <Info width={14} height={14} />
                                                    <p className="text-[12px] text-secondary-600 mr-2">
                                                        لینک موردنظر تا
                                                        {info.day > 0 && <span> {info.day} روز </span>}
                                                        {(info.hour > 0 || info.minute > 0) && info.day > 0 && <span>و</span>}
                                                        {info.hour > 0 && <span> {info.hour} ساعت </span>}
                                                        {info.hour > 0 && info.minute > 0 && <span>و</span>}
                                                        {info.minute > 0 && <span> {info.minute} دقیقه</span>} دیگر در دسترس است
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    {formErrors.minute !== "" && <p className="text-[12px] text-red-500">{formErrors.minute}</p>}
                                                    {formErrors.hour !== "" && <p className="text-[12px] text-red-500">{formErrors.hour}</p>}
                                                    {formErrors.day !== "" && <p className="text-[12px] text-red-500">{formErrors.day}</p>}
                                                    {formErrors.time !== "" && <p className="text-[12px] text-red-500">{formErrors.time}</p>}
                                                </>
                                            )}
                                        </>
                                    )}
                                    <div className="flex justify-between items-center relative">
                                        <Toggle enabled={enabled} setEnabled={setEnabled} />
                                        {!enabled && (
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="!text-[17px] text-primary-600 text-center font-bold bg-[#EFF0F6] inline-block w-[32px] leading-8 rounded-[8px] cursor-pointer"
                                                    onClick={() => handleChangeValue(info.expiration_count + 1, "expiration_count")}
                                                >
                                                    +
                                                </span>
                                                <NumberInput
                                                    parentClassName="w-[38px]"
                                                    inputClassName="!p-0 !mx-aut0  !text-center h-[32px] border-0 !border-b-[1px] !border-secondary-300 !rounded-none !bg-transparent !text-[16px] font-medium"
                                                    value={info.expiration_count ? info.expiration_count : 1}
                                                    onChange={(value) => handleChangeValue(Number(value), "expiration_count")}
                                                    onBlur={(value) => handleFormValidation(value, "expiration_count")}
                                                    error={formErrors.expiration_count}
                                                    showError={false}
                                                />
                                                <span
                                                    className="!text-[17px] text-primary-600 text-center font-bold bg-[#EFF0F6] inline-block w-[32px] leading-8 rounded-[8px] cursor-pointer"
                                                    onClick={() => handleChangeValue(info.expiration_count - 1, "expiration_count")}
                                                >
                                                    -
                                                </span>
                                            </div>
                                        )}
                                        <p className="absolute top-full text-[12px] text-red-500">{formErrors.expiration_count}</p>
                                    </div>

                                    {!(data || showPublicLinkEditModal) && (
                                        <div className="flex items-center md:items-start justify-start">
                                            <CheckBox label="" onChange={(value) => setApprove(value)} defaultValue={approve} />
                                            <p className="text-secondary-500 text-xs md:mt-1 leading-5 text-justify lg:text-sm lg:leading-6">
                                                ایجاد لینک دسترسی به منزله پذیرش و رعایت{" "}
                                                <a
                                                    href={`${ENVIRONMENT.landingBaseUrl}rules`}
                                                    target="_blank"
                                                    className="text-primary-600 underline"
                                                    rel="noreferrer"
                                                >
                                                    قوانین ابرهمراهی
                                                </a>
                                                ، قوانین کپی‌ رایت و قوانین رسمی کشور است.
                                            </p>
                                        </div>
                                    )}

                                    <Button
                                        ref={buttonRef}
                                        text={data || showPublicLinkEditModal ? "ویرایش لینک" : "ایجاد لینک"}
                                        className="w-full mt-7"
                                        autoFocus={false}
                                        onClick={data || showPublicLinkEditModal ? handleEditLink : handleCreateLink}
                                        disable={data || showPublicLinkEditModal ? false : !approve ? true : !fileHasLinkCapacity()}
                                    />
                                </div>
                            ) : (
                                <div
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.stopPropagation();
                                            buttonRef.current.click();
                                        } else if (e.key === "Escape") {
                                            handleClosePublicLinkModal();
                                        }
                                    }}
                                >
                                    <button autoFocus={true} ref={hiddenInput} className="w-0 h-0 absolute outline-none" />
                                    {selected.payload === 2 && (
                                        <div className="flex items-center mt-4">
                                            <Info width={14} height={14} />
                                            <p className="text-[12px] text-secondary-600 mr-2">
                                                لینک موردنظر تا
                                                {info.day > 0 && <span> {info.day} روز </span>}
                                                {(info.hour > 0 || info.minute > 0) && info.day > 0 && <span>و</span>}
                                                {info.hour > 0 && <span> {info.hour} ساعت </span>}
                                                {info.hour > 0 && info.minute > 0 && <span>و</span>}
                                                {info.minute > 0 && <span> {info.minute} دقیقه</span>} دیگر در دسترس است
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex justify-between bg-[#EFF0F6] rounded-[10px] p-4 mt-5">
                                        <button
                                            className="border-l-2 border-secondary-300 pl-4 cursor-pointer"
                                            onClick={() => handleCopyPublicLink(info.link, false)}
                                            autoFocus
                                        >
                                            <Copy />
                                        </button>
                                        <p
                                            className="text-secondary-400 w-full whitespace-nowrap overflow-hidden text-ellipsis"
                                            style={{ direction: "ltr" }}
                                        >
                                            {info.link}
                                        </p>
                                    </div>

                                    <div className="table mt-6 text-primary-600 font-medium" onClick={() => handleEditPublicLink(info)}>
                                        <span className="flex items-center gap-2">
                                            <Edit className="cursor-pointer" />
                                            <span className="cursor-pointer">ویرایش لینک</span>
                                        </span>
                                    </div>

                                    <div className="table mt-6 text-[#EF4444] font-medium" onClick={() => setShowRemovePublicLinkModal(true)}>
                                        <span className="flex items-center gap-2">
                                            <Trash className="cursor-pointer" />
                                            <span className="cursor-pointer">حذف لینک</span>
                                        </span>
                                    </div>
                                    <Button
                                        text="کپی‌کردن لینک"
                                        className="w-full mt-6"
                                        onClick={() => handleCopyPublicLink(info.link, true)}
                                        ref={buttonRef}
                                        autoFocus={true}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <PublicLinkApproved approved={selectedResources?.[0]?.cp_approved} />
                    )}
                </div>
            </Dialog>
        </>
    );
};

export default PublicLinkModal;
